import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Section } from "../components/Common";
import PractitionerRecommendations from "../components/PractitionerRecommendations";
import { ArrowRight, Sparkles, Heart, Brain, Apple, Moon, Trophy, Users } from "lucide-react";
import AppCta from "../components/AppCta";

/**
 * Wellness Hub gateway — the top of the Wellness ecosystem branch.
 * Routes to the six wellness subtopics.
 */
export default function WellnessOverviewPage() {
  const { t } = useTranslation();
  const cards = [
    {
      to: "/wellness/mens", title: t("nav.wellness_mens"),
      blurb: t("wellness_overview.cards.mens_blurb"),
      image: "https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?auto=format&fit=crop&w=900&q=80",
      icon: Users,
    },
    {
      to: "/wellness/womens", title: t("nav.wellness_womens"),
      blurb: t("wellness_overview.cards.womens_blurb"),
      image: "https://images.unsplash.com/photo-1611077531823-c4d3ee05ae6a?auto=format&fit=crop&w=900&q=80",
      icon: Heart,
    },
    {
      to: "/wellness/mental", title: t("nav.wellness_mental"),
      blurb: t("wellness_overview.cards.mental_blurb"),
      image: "https://images.unsplash.com/photo-1490730141103-6cac27aaab94?auto=format&fit=crop&w=900&q=80",
      icon: Brain,
    },
    {
      to: "/wellness/nutrition", title: t("nav.wellness_nutrition"),
      blurb: t("wellness_overview.cards.nutrition_blurb"),
      image: "https://images.unsplash.com/photo-1490645935967-10de6ba17061?auto=format&fit=crop&w=900&q=80",
      icon: Apple,
    },
    {
      to: "/wellness/sleep", title: t("nav.wellness_sleep"),
      blurb: t("wellness_overview.cards.sleep_blurb"),
      image: "https://images.unsplash.com/photo-1455642305367-68834a9d4337?auto=format&fit=crop&w=900&q=80",
      icon: Moon,
    },
    {
      to: "/wellness/sports", title: t("nav.wellness_sports"),
      blurb: t("wellness_overview.cards.sports_blurb"),
      image: "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?auto=format&fit=crop&w=900&q=80",
      icon: Trophy,
    },
  ];
  return (
    <div data-testid="page-wellness-overview">
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&w=1800&q=80"
            alt=""
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/65 to-black/30" />
        </div>
        <div className="relative app-container py-20 md:py-28">
          <div className="max-w-2xl">
            <div className="eyebrow mb-3" data-testid="wellness-overview-eyebrow">{t("wellness_overview.eyebrow")}</div>
            <h1 className="font-display text-4xl md:text-5xl leading-tight max-w-[20ch]" data-testid="wellness-overview-title">{t("wellness_overview.title")}</h1>
            <p className="text-white/80 mt-5 leading-relaxed max-w-[60ch] body-copy text-lg">
              {t("wellness_overview.subtitle")}
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/dosha" className="btn-primary text-sm inline-flex items-center gap-2" data-testid="wellness-overview-cta-dosha">
                <Sparkles className="w-4 h-4" /> {t("dosha.cta_short")}
              </Link>
              <Link to="/ask-donny" className="btn-ghost text-sm" data-testid="wellness-overview-cta-donny">{t("nav.ask_donny")}</Link>
            </div>
          </div>
        </div>
      </section>

      <Section eyebrow={t("wellness_overview.explore_eyebrow")} title={t("wellness_overview.explore_title")} subtitle={t("wellness_overview.explore_subtitle")}>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="wellness-overview-grid">
          {cards.map((c) => {
            const Icon = c.icon;
            return (
              <Link
                key={c.to}
                to={c.to}
                className="card-surface overflow-hidden group flex flex-col hover:border-[#C8A24A]/40 transition-colors"
                data-testid={`wellness-card-${c.to.split("/").pop()}`}
              >
                <div className="aspect-[16/10] overflow-hidden relative">
                  <img src={c.image} alt={c.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                  <div className="absolute bottom-3 left-3 right-3 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-black/55 border border-[#C8A24A]/45 backdrop-blur grid place-items-center">
                      <Icon className="w-4 h-4 gold-text" />
                    </div>
                    <div className="font-display text-lg leading-snug">{c.title}</div>
                  </div>
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <p className="text-sm text-white/70 leading-relaxed flex-1">{c.blurb}</p>
                  <div className="mt-3 text-xs gold-text inline-flex items-center gap-1">
                    {t("ecosystem.explore")} <ArrowRight className="w-3 h-3" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </Section>

      <PractitionerRecommendations
        eyebrow={t("wellness_overview.recs_eyebrow")}
        title={t("wellness_overview.recs_title")}
        subtitle={t("wellness_overview.recs_subtitle")}
        limit={3}
        testid="wellness-overview-recs"
      />

      <AppCta variant="wellness" testid="wellness-app-cta" />
    </div>
  );
}
